import { MessageSquare, Plus } from "lucide-react";
import { Button } from "@/client/components/ui/button";
import { ScrollArea } from "@/client/components/ui/scroll-area";
import { formatRelativeTime } from "@/client/hooks/use-document";
import { useChats, useCreateChat } from "@/client/hooks/use-chats";
import { useTabs } from "@/client/hooks/use-tabs-context";
import { cn } from "@/client/lib/utils";
import { useAgentSuggestions } from "@/client/hooks/use-agent-suggestions";

/** The navigator's Chats: the story's shared AI conversations. */
export function ChatsPanel({ storyId }: { storyId: string }) {
  const { data: chats } = useChats(storyId);
  const { mutateAsync: createChat, isPending } = useCreateChat();
  const { openChatTab, openTab, activeTab } = useTabs();
  const { suggestions, loading, error, refresh } = useAgentSuggestions(storyId);

  const newChat = async () => {
    try {
      const chat = await createChat({ storyId });
      openChatTab(chat.id, chat.title);
    } catch {
      // Reported by the collection
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div data-region="panel-header" className="flex items-center justify-between gap-2 border-b p-3">
        <h2 className="text-sm font-semibold">Chats</h2>
        <Button variant="ghost" size="icon" className="size-7" onClick={newChat} disabled={isPending} aria-label="New chat">
          <Plus className="size-4" />
        </Button>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <section className="border-b p-2">
          <h3 className="px-2 py-1 text-xs font-medium text-muted-foreground">Agent suggestions</h3>
          {loading ? <p className="px-2 py-2 text-xs text-muted-foreground">Loading…</p> : error ?
            <div className="px-2 py-2 text-xs"><p role="alert">{error}</p><button type="button" className="mt-1 underline" onClick={() => void refresh()}>Try again</button></div> :
            suggestions.length === 0 ? <p className="px-2 py-2 text-xs text-muted-foreground">Suggestions from connected agents appear here for review.</p> :
            <ul>{suggestions.map((s) => <li key={s.id}><button type="button" className={cn("flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent",
              activeTab?.type === "proposal" && activeTab.entityId === s.id && "bg-primary/10 text-primary")}
              onClick={() => openTab({ type: "proposal", entityId: s.id, title: `Suggestion: ${s.sceneTitle}` })}>
              <span className="min-w-0 flex-1"><span className="block truncate">{s.sceneTitle}</span><span className="block truncate text-xs text-muted-foreground">{s.clientName}</span></span>
              <span className="shrink-0 text-[11px] text-muted-foreground">{s.status === "pending" ? "Review" : s.status}</span>
            </button></li>)}</ul>}
        </section>
        {chats.length === 0 ? (
          <div className="space-y-3 p-6 text-center text-sm text-muted-foreground">
            <p>No conversations yet. Everyone on the story sees them.</p>
            <Button size="sm" variant="outline" onClick={newChat} disabled={isPending}>
              <Plus />
              New chat
            </Button>
          </div>
        ) : (
          <ul className="space-y-0.5 p-2">
            {chats.map((chat) => (
              <li key={chat.id}>
                <button
                  type="button"
                  data-region="row"
                  data-active={(activeTab?.type === "chat" && activeTab.entityId === chat.id) || undefined}
                  onClick={() => openChatTab(chat.id, chat.title)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                    activeTab?.type === "chat" && activeTab.entityId === chat.id && "bg-primary/10 text-primary"
                  )}
                >
                  <MessageSquare className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{formatRelativeTime(chat.updatedAt)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}
